import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    async function onSubmit(e) {
        e.preventDefault();
        setLoading(true);
        try {
            const user = await login(email.trim(), password);
            toast.success(`Bienvenue, ${user.first_name}.`);
            const target = location.state?.from && location.state.from !== "/login"
                ? location.state.from
                : "/dashboard";
            navigate(target, { replace: true });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }

    return (
        <AuthShell testId="login-page">
            <Link to="/" className="inline-flex items-center gap-1 text-inkSoft hover:text-ink text-sm mb-8" data-testid="back-home">
                <ArrowLeft className="w-4 h-4" /> Accueil
            </Link>
            <h1 className="font-serif text-3xl md:text-4xl tracking-tight" data-testid="login-title">
                Ravi de vous revoir.
            </h1>
            <p className="mt-2 text-sm text-inkSoft">
                Connectez-vous pour continuer votre programme.
            </p>

            <form className="mt-8 space-y-5" onSubmit={onSubmit} data-testid="login-form">
                <Field label="Email" id="email">
                    <input
                        id="email"
                        type="email"
                        required
                        autoComplete="email"
                        data-testid="login-email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full h-11 rounded-xl border border-lexiBorder bg-paper px-4 focus:outline-none focus:ring-2 focus:ring-terracotta"
                    />
                </Field>
                <Field label="Mot de passe" id="password">
                    <input
                        id="password"
                        type="password"
                        required
                        autoComplete="current-password"
                        minLength={1}
                        data-testid="login-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full h-11 rounded-xl border border-lexiBorder bg-paper px-4 focus:outline-none focus:ring-2 focus:ring-terracotta"
                    />
                </Field>

                <button
                    type="submit"
                    disabled={loading}
                    data-testid="login-submit"
                    className="w-full h-11 rounded-full bg-terracotta hover:bg-terracottaDark text-white text-sm font-medium disabled:opacity-60 active:scale-[0.98] transition-transform inline-flex items-center justify-center gap-2"
                >
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    Se connecter
                </button>
            </form>

            <p className="mt-8 text-sm text-inkSoft">
                Pas encore de compte ?{" "}
                <Link to="/register" className="text-terracotta hover:underline" data-testid="link-register">
                    Créer un compte
                </Link>
            </p>
        </AuthShell>
    );
}

export function Field({ label, id, children }) {
    return (
        <label htmlFor={id} className="block">
            <span className="text-[11px] tracking-[0.22em] uppercase text-inkSoft font-semibold">
                {label}
            </span>
            <div className="mt-1.5">{children}</div>
        </label>
    );
}

export function AuthShell({ children, testId }) {
    return (
        <div className="min-h-screen bg-paper flex items-center justify-center px-4 py-12" data-testid={testId}>
            <div className="w-full max-w-md bg-white border border-lexiBorder rounded-2xl p-8 md:p-10">
                {children}
            </div>
        </div>
    );
}
